import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { geldText } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { applyPriceRefresh, fetchPriceRefresh } from '../api/calculation-pricing.api'
import type { CalcPriceRefreshLine } from '../types/calculation.types'

const datum = (s: string | null) => (s ? new Date(s).toLocaleDateString('de-DE') : '—')

/**
 * AP-37 4.10 — **Preise aktualisieren**: Artikelzeilen mit altem oder
 * geändertem Preis, Vorschau alt → neu je Zeile; übernommen wird, was angehakt
 * ist. Vorbelegt sind die Zeilen, deren Preis sich geändert hat — ein nur
 * alter, aber gleicher Preis bekommt dabei sein neues Datum.
 */
export function PreiseAktualisierenDialog({ kalkulationId, darf, onClose, onDone }: { kalkulationId: string; darf: boolean; onClose: () => void; onDone: (n: number) => void }) {
  const [zeilen, setZeilen] = useState<CalcPriceRefreshLine[] | null>(null)
  const [tage, setTage] = useState(0)
  const [haken, setHaken] = useState<Set<string>>(new Set())
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    fetchPriceRefresh(kalkulationId)
      .then((r) => {
        setZeilen(r.lines)
        setTage(r.days)
        setHaken(new Set(r.lines.filter((l) => l.changed).map((l) => l.lineId)))
      })
      .catch((e) => setFehler(apiErrorText(e, 'Der Preisstand ließ sich nicht laden.')))
  }, [kalkulationId])

  const uebernehmen = async () => {
    try {
      const r = await applyPriceRefresh(kalkulationId, [...haken])
      onDone(r.updated)
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Preise ließen sich nicht übernehmen.'))
    }
  }
  const alle = zeilen?.length ? haken.size === zeilen.length : false

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="w-full max-w-4xl rounded-lg border border-border p-6 shadow-xl" style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}>
        <div className="mb-3 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-primary">Preise aktualisieren</h2>
            <p className="text-xs text-muted-foreground">Artikelzeilen mit Preis älter als {tage} Tage oder mit geändertem Preis des Vorzugslieferanten.</p>
          </div>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Schließen">
            <X className="h-5 w-5" />
          </button>
        </div>
        {fehler && <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}
        {!zeilen ? (
          <p className="text-sm text-muted-foreground">Lädt …</p>
        ) : zeilen.length === 0 ? (
          <p className="text-sm text-muted-foreground">Alle Artikelpreise sind aktuell.</p>
        ) : (
          <div className="max-h-[60vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted/60 text-xs text-muted-foreground">
                <tr>
                  <th className="px-2 py-1.5 text-left">
                    <input type="checkbox" checked={alle} disabled={!darf} onChange={() => setHaken(alle ? new Set() : new Set(zeilen.map((z) => z.lineId)))} aria-label="alle" />
                  </th>
                  <th className="px-2 py-1.5 text-left font-normal">Position</th>
                  <th className="px-2 py-1.5 text-left font-normal">Kostenzeile</th>
                  <th className="px-2 py-1.5 text-right font-normal">alt</th>
                  <th className="px-2 py-1.5 text-left font-normal">Stand</th>
                  <th className="px-2 py-1.5 text-right font-normal">neu</th>
                  <th className="px-2 py-1.5 text-left font-normal">Lieferant</th>
                </tr>
              </thead>
              <tbody>
                {zeilen.map((z) => (
                  <tr key={z.lineId} className="border-t border-border/60">
                    <td className="px-2 py-1">
                      <input
                        type="checkbox"
                        checked={haken.has(z.lineId)}
                        disabled={!darf}
                        onChange={() => setHaken((h) => (h.has(z.lineId) ? new Set([...h].filter((x) => x !== z.lineId)) : new Set([...h, z.lineId])))}
                        aria-label={z.name}
                      />
                    </td>
                    <td className="max-w-[12rem] truncate px-2 py-1 text-muted-foreground">{z.positionLabel}</td>
                    <td className="px-2 py-1">
                      {z.name} <span className="text-xs text-muted-foreground">{z.unitCode}</span>
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums">
                      {geldText(z.oldPrice) || '—'}
                      {z.oldPriceUnit !== 1 && <span className="text-xs text-muted-foreground"> / {z.oldPriceUnit}</span>}
                    </td>
                    <td className="px-2 py-1 text-xs text-muted-foreground">
                      {datum(z.oldPriceDate)}
                      {z.ageDays != null && ` (${z.ageDays} T.)`}
                    </td>
                    <td className={`px-2 py-1 text-right tabular-nums ${z.changed ? 'font-medium text-sky-800 dark:text-sky-300' : ''}`}>
                      {geldText(z.newPrice)}
                      {z.newPriceUnit !== 1 && <span className="text-xs text-muted-foreground"> / {z.newPriceUnit}</span>}
                    </td>
                    <td className="px-2 py-1 text-xs">{z.supplier.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="rounded-md border border-border px-4 py-2 text-sm" onClick={onClose}>
            Abbrechen
          </button>
          {darf && (
            <button type="button" className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50" disabled={!haken.size} onClick={() => void uebernehmen()}>
              {haken.size} Preis(e) übernehmen
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
